<template>
  <div class="template-selection-container">
    <!-- AI Assistant Dialog -->
    <AIAssistant
      v-model="showAIAssistant"
      :currentDocument="null"
      @template-selected="handleTemplateSelection"
    />
    <div class="steps-header">
      <div class="step-item active">
        <div class="step-number active">1</div>
        <span class="step-text">{{ $t("welcome.step1_title") }}</span>
      </div>

      <div class="step-connector"></div>

      <div class="step-item">
        <div class="step-number">2</div>
        <span class="step-text">{{ $t("welcome.step2_title") }}</span>
      </div>

      <div class="step-connector"></div>

      <div class="step-item">
        <div class="step-number">3</div>
        <span class="step-text">{{ $t("welcome.step3_title") }}</span>
      </div>
    </div>

    <div class="content-wrapper">
      <h1 class="page-title">{{ $t("templateSelection.title") }}</h1>

      <p class="description">
        {{ $t("templateSelection.description") }}
      </p>

      <!-- AI Assistant Button -->
      <div class="ai-assistant-section">
        <button @click="showAIAssistant = true" class="ai-assistant-btn">
          <img src="@/assets/icons/star.svg" alt="star" class="star-icon" />
          {{ $t("templateSelection.askAI") }}
        </button>
      </div>

      <!-- Separator Text -->
      <div class="separator-text">
        {{ $t("templateSelection.or") }}
      </div>

      <!-- Template Selection with Integrated Search -->
      <div class="template-selector">
        <v-combobox
          v-model="selectedTemplate"
          :items="templateItems"
          :label="$t('templateSelection.selectTemplate')"
          variant="outlined"
          density="comfortable"
          class="template-dropdown"
          :loading="isLoading"
          item-title="title"
          item-value="value"
          return-object
          clearable
          :search="searchQuery"
          :menu-props="{
            maxHeight: '400px',
            contentClass: 'template-dropdown-menu',
            offset: 8,
            width: 'auto',
            location: 'bottom start',
            attach: false,
            openOnClick: false,
            closeOnContentClick: false,
          }"
          @update:search="onSearchUpdate"
          @update:menu="onMenuToggle"
          @update:model-value="onTemplateSelect"
        >

          <template v-slot:item="{ props, item }">
            <template v-if="item.raw.header">
              <v-list-subheader class="template-section-header">
                <v-icon
                  :icon="item.raw.header === t('templateSelection.freeDocuments') ? 'mdi-heart' : 'mdi-crown'"
                  :color="item.raw.header === t('templateSelection.freeDocuments') ? 'green' : 'orange'"
                  size="18"
                  class="mr-2"
                />
                {{ item.raw.header }}
              </v-list-subheader>
            </template>
            <template v-else-if="item.raw.divider">
              <v-divider class="my-2"></v-divider>
            </template>
            <template v-else>
              <v-list-item v-bind="props" class="template-list-item">
                <template v-slot:title>
                  <span class="template-name">{{ item.raw.title }}</span>
                </template>
              </v-list-item>
            </template>
          </template>

          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>{{ $t("templateSelection.noResults") }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-combobox>
      </div>

      <div class="action-button">
        <button @click="proceedToQuestions" :disabled="!selectedTemplate" class="proceed-button">
          {{ $t("templateSelection.proceed") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import AIAssistant from "@/components/AIAssistant.vue";
import axios from "axios";
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const selectedTemplate = ref(null);
const showAIAssistant = ref(false);
const templates = ref([]);
const filteredTemplates = ref([]);
const searchQuery = ref("");
const isLoading = ref(false);

// Helper function to determine if template is free
const isTemplateFree = (template) => {
  // Based on API data: is_payable field determines if template requires payment
  // is_payable: true means it's a PAID template
  // is_payable: false or undefined means it's FREE

  if (template.is_payable === true) {
    return false; // It's a paid template
  }

  // If is_payable is false or undefined, it's free
  return true;
};

// Computed property for template dropdown items with free/paid grouping
const templateItems = computed(() => {
  const freeTemplates = filteredTemplates.value
    .filter((template) => isTemplateFree(template))
    .map((template) => ({
      title: template.name,
      value: template.id,
      template: template,
      isFree: true,
    }));

  const paidTemplates = filteredTemplates.value
    .filter((template) => !isTemplateFree(template))
    .map((template) => ({
      title: template.name,
      value: template.id,
      template: template,
      isFree: false,
    }));

  const items = [];

  if (freeTemplates.length > 0) {
    items.push({ header: t("templateSelection.freeDocuments") });
    items.push(...freeTemplates);
  }

  if (paidTemplates.length > 0) {
    if (items.length > 0) items.push({ divider: true });
    items.push({ header: t("templateSelection.premiumDocuments") });
    items.push(...paidTemplates);
  }

  return items;
});

// Search functionality
const searchTemplates = () => {
  if (!searchQuery.value || !searchQuery.value.trim()) {
    filteredTemplates.value = templates.value;
    return;
  }

  const query = searchQuery.value.toLowerCase().trim();
  filteredTemplates.value = templates.value.filter(
    (template) =>
      template.name.toLowerCase().includes(query) ||
      (template.description && template.description.toLowerCase().includes(query)),
  );
};

// Handle search input from v-select
const onSearchUpdate = (value) => {
  searchQuery.value = value;
  searchTemplates();
};

// Clear search function for manual clearing (currently unused but kept for potential future use)
// eslint-disable-next-line no-unused-vars
const clearSearch = () => {
  searchQuery.value = "";
  filteredTemplates.value = templates.value;
  selectedTemplate.value = null;
};

// Fetch templates from API
const getTemplatesList = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get("api/templates", {
      params: {
        page: 1,
        per_page: 100, // Get more templates for selection
        sort: "-documents_count",
      },
    });

    // Log template data for debugging
    console.log('Templates loaded:', response.data.data);
    response.data.data.forEach(template => {
      console.log(`Template: ${template.name}`);
      console.log(`  - is_payable: ${template.is_payable}`);
      console.log(`  - determined as: ${isTemplateFree(template) ? 'FREE' : 'PAID'}`);
      console.log('---');
    });

    templates.value = response.data.data;
    filteredTemplates.value = response.data.data;
  } catch (error) {
    console.error("Error fetching templates:", error);
    // Fallback to simple templates if API fails
    templates.value = [
      { id: 1, name: 'Umowa najmu', description: 'Szablon umowy najmu lokalu', is_free: true, is_paid: false },
      { id: 2, name: 'Umowa o pracę', description: 'Szablon umowy o pracę', is_free: true, is_paid: false },
      { id: 3, name: 'Umowa biznesowa', description: 'Szablon umowy biznesowej', is_free: false, is_paid: true },
    ];
    filteredTemplates.value = templates.value;
  } finally {
    isLoading.value = false;
  }
};

const handleTemplateSelection = (template) => {
  selectedTemplate.value = template;
  showAIAssistant.value = false;
};

// eslint-disable-next-line no-unused-vars
const onMenuToggle = (isOpen) => {
  // Remove the clearing of search when dropdown closes
  // This was causing the input to lose focus and clear after typing one character
};

// Only blur inputs when a template is actually selected (not during typing)
const onTemplateSelect = (value) => {
  if (value && value.template) {
    // Only blur when a template object is selected, not during search typing
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        activeElement.blur();
      }
      // Also blur any Vuetify input elements
      const vuetifyInputs = document.querySelectorAll(".v-select input, .v-text-field input");
      vuetifyInputs.forEach((input) => input.blur());
    }, 100);
  }
};

const proceedToQuestions = () => {
  if (selectedTemplate.value) {
    // Store selected template in Vuex store
    store.commit("shared/setSelectedTemplate", selectedTemplate.value);

    // Extract template ID properly
    let templateId;
    if (selectedTemplate.value.template && selectedTemplate.value.template.id) {
      templateId = selectedTemplate.value.template.id;
    } else if (selectedTemplate.value.value) {
      templateId = selectedTemplate.value.value;
    } else if (selectedTemplate.value.id) {
      templateId = selectedTemplate.value.id;
    } else {
      templateId = selectedTemplate.value;
    }

    router.push({
      name: "document-questions",
      query: { template: templateId },
    });
  }
};

// Handle keyboard navigation - spacebar to proceed when template is selected
const handleKeyPress = (event) => {
  // Check if spacebar is pressed
  if (event.key === " " || event.key === "Space" || event.code === "Space") {
    // Check if we're not in an input field or textarea
    const activeElement = document.activeElement;
    const isInputField =
      activeElement &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable);

    // If not in an input field and a template is selected, proceed
    if (!isInputField && selectedTemplate.value) {
      event.preventDefault();
      proceedToQuestions();
    }
  }
};

onMounted(() => {
  getTemplatesList();

  // Add keyboard event listener
  window.addEventListener("keydown", handleKeyPress);
});

// Clean up event listener when component is unmounted
onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyPress);
});
</script>

<style lang="scss" scoped>
.template-selection-container {
  min-height: 100vh;
  background: #fafafa;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    padding-bottom: 10rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
    padding-bottom: 12rem;
  }

  @media (max-width: 360px) {
    padding: 1rem;
    padding-bottom: 12rem;
  }
}

.steps-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4rem;
  gap: 2rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 640px) {
    gap: 0.75rem;
    margin-bottom: 2rem;
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding: 0;
  }
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &.active {
    .step-text {
      color: #1e3a8a;
      font-weight: 600;
    }
  }
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e5e5;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;

  &.active {
    background: linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%);
    color: white;
  }
}

.step-text {
  font-family: "Archivo", sans-serif;
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;

  @media (max-width: 640px) {
    display: none;
  }
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #e5e5e5;

  @media (max-width: 768px) {
    width: 40px;
  }

  @media (max-width: 640px) {
    width: 30px;
  }

  @media (max-width: 480px) {
    width: 20px;
  }
}

.content-wrapper {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  padding: 0 1rem;

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }
}

.page-title {
  font-family: "Archivo", sans-serif;
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: #1e293b;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }
}

.description {
  font-family: "Archivo", sans-serif;
  font-size: 18px;
  line-height: 28px;
  color: #64748b;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 1.5rem;
  }
}

.ai-assistant-section {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
}

.ai-assistant-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 56px;
  padding: 0 40px;
  background: white;
  color: #0023EF;
  border: 3px solid #0023EF;
  border-radius: 100px;
  font-family: "Archivo", sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25), 0 0 0 1px rgba(0, 35, 239, 0.1);

  .star-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 35, 239, 0.1), transparent);
    transition: transform 0.6s ease;
  }

  &:hover {
    transform: translateY(-1px);
    background: #f8faff;
    border-color: #0019CC;
    box-shadow: 0 6px 16px rgba(0, 35, 239, 0.3), 0 0 0 2px rgba(0, 35, 239, 0.15);

    &::before {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 8px rgba(0, 35, 239, 0.2);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 320px;
    padding: 0 32px;
    font-size: 15px;
    height: 52px;
  }
}

.separator-text {
  font-family: "Archivo", sans-serif;
  font-size: 16px;
  color: #64748b;
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 1.25rem;
  }
}

.template-selector {
  margin-bottom: 2rem;
  position: relative;
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .template-dropdown {
    font-family: "Archivo", sans-serif;
    width: 100%;

    ::v-deep .v-field {
      background: white;
      border-radius: 12px;
    }

    ::v-deep .v-field__input {
      padding: 16px;
      font-size: 16px;
    }

    // Force the overlay to match this width
    ::v-deep .v-overlay__content {
      max-width: 720px !important;
      width: 720px !important;
    }
  }
}


.action-button {
  margin-bottom: 2rem;
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

.proceed-button {
  height: 56px;
  padding: 0 40px;
  background: #0023ef;
  color: white;
  border: none;
  border-radius: 100px;
  font-family: "Archivo", sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: #0019cc;
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(0, 35, 239, 0.25);

    &::before {
      transform: translateX(100%);
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
    transition: transform 0.6s;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.2);
  }

  &:disabled {
    background: rgba(0, 35, 239, 0.6);
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 320px;
    padding: 0 32px;
    font-size: 15px;
  }
}

// Styles for template grouping
.template-section-header {
  font-family: "Archivo", sans-serif !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  color: #374151 !important;
  background: #f9fafb !important;
  padding: 8px 16px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.template-list-item {
  font-family: "Archivo", sans-serif;
  min-height: 52px !important;
  padding: 8px 16px !important;

  .template-name {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
    line-height: 1.4;
    word-break: break-word;
    white-space: normal !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    max-width: calc(100% - 80px); // Leave space for PREMIUM chip
    flex: 1;
    min-width: 0; // Critical for text wrapping in flex containers
  }

  .premium-chip {
    font-size: 10px !important;
    font-weight: 600 !important;
    height: 18px !important;
    padding: 0 6px !important;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 1px;
  }

  &:hover {
    background: #f8fafc !important;
  }
}

// Override Vuetify styles for better appearance
::v-deep .v-list-subheader {
  min-height: auto !important;
  height: auto !important;
}

::v-deep .v-list-item--density-default.v-list-item--one-line {
  min-height: 52px !important;
}

// Force dropdown menu to appear below and match select input width
::v-deep .template-dropdown-menu {
  z-index: 2010 !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
  background: white !important;
  max-width: 720px !important;
  width: 720px !important;
  overflow-x: hidden !important;

  // Ensure proper spacing from input
  margin-top: 8px !important;

  @media (max-width: 768px) {
    max-width: calc(100vw - 4rem) !important;
    width: calc(100vw - 4rem) !important;
  }

  @media (max-width: 480px) {
    max-width: calc(100vw - 2rem) !important;
    width: calc(100vw - 2rem) !important;
  }

  .v-list {
    width: 100% !important;
    max-width: 100% !important;
    background: white;
    overflow-x: hidden !important;
  }

  .v-list-item {
    width: 100% !important;
    max-width: 100% !important;

    .v-list-item__content {
      overflow: hidden !important;
      width: 100% !important;
      max-width: 100% !important;
      flex-wrap: wrap !important;

      .v-list-item-title {
        // Enable 2-line wrapping instead of ellipsis
        white-space: normal !important;
        overflow: hidden !important;
        display: -webkit-box !important;
        -webkit-line-clamp: 2 !important;
        -webkit-box-orient: vertical !important;
        line-height: 1.4 !important;
        word-break: break-word !important;
        width: 100% !important;
        max-width: 100% !important;
      }
    }

    // Target the title wrapper specifically
    .v-list-item-title {
      white-space: normal !important;
      overflow: hidden !important;
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

// Override Vuetify dropdown positioning and width - AGGRESSIVE APPROACH
::v-deep .v-overlay {
  .v-overlay__content.template-dropdown-menu {
    // Force dropdown to match 720px max width
    max-width: 720px !important;
    width: 720px !important;
    min-width: 0 !important;

    @media (max-width: 768px) {
      max-width: calc(100vw - 4rem) !important;
      width: calc(100vw - 4rem) !important;
    }

    @media (max-width: 480px) {
      max-width: calc(100vw - 2rem) !important;
      width: calc(100vw - 2rem) !important;
    }
  }
}

// Also target v-overlay__content directly without needing the class
::v-deep .v-select__content,
::v-deep .v-menu__content {
  max-width: 720px !important;
  width: 720px !important;

  @media (max-width: 768px) {
    max-width: calc(100vw - 4rem) !important;
    width: calc(100vw - 4rem) !important;
  }

  @media (max-width: 480px) {
    max-width: calc(100vw - 2rem) !important;
    width: calc(100vw - 2rem) !important;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .template-selector {
    max-width: calc(100% - 2rem);
  }
}

@media (max-width: 480px) {
  .template-selector {
    max-width: 100%;
  }
}
</style>

<style lang="scss">
// GLOBAL STYLES (non-scoped) - Nuclear option to force dropdown width
// THIS IS THE KEY STYLE THAT CONTROLS THE DROPDOWN WIDTH - adjust the px value here
.template-dropdown-menu.v-overlay__content {
  max-width: 570px !important;
  width: 570px !important;
  min-width: 0 !important;

  // Mobile responsive adjustments
  @media (max-width: 768px) {
    max-width: calc(100vw - 7rem) !important;
    width: calc(100vw - 7rem) !important;
  }

  @media (max-width: 480px) {
    max-width: calc(100vw - 2rem) !important;
    width: calc(100vw - 2rem) !important;
  }

  @media (max-width: 360px) {
    max-width: calc(100vw - 1.5rem) !important;
    width: calc(100vw - 1.5rem) !important;
  }
}

.template-selection-container {
  .v-select__content,
  .v-menu__content,
  .v-list {
    max-width: 570px !important;

    @media (max-width: 768px) {
      max-width: calc(100vw - 3rem) !important;
    }

    @media (max-width: 480px) {
      max-width: calc(100vw - 2rem) !important;
    }

    @media (max-width: 360px) {
      max-width: calc(100vw - 1.5rem) !important;
    }
  }
}
</style>
